import { defineComponent as _defineComponent } from 'vue'
import TTMassEntryTable2 from "@/components/modules/entry/TTMassEntryTable2.vue";
import TTEditRowsModal, {
  TTEditForm,
} from "@/components/modules/entry/TTEditRowsModal.vue";
import AccountSelectModal from "@/components/modules/account/AccountSelectModal.vue";
import { apolloClient, queries, mutations } from "@/apollo";
import { Release, User, TtMode, TtPage } from "@/CodegenTypes";
import { TTEntryInput, TTDay } from "@/types";
import { parseTtEntryInputs } from "@/helpers/tt/parseTtEntryInputs";
import TTCopyBetModal from "@/components/modules/entry/TTCopyBetModal.vue";

import { clamp, compact } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";
import { errorMutationNotification } from "@/utils";
import { calculateTtEntries } from "@/helpers/tt/calculateTtEntries";
import { validateTTNumbers } from "@/helpers/tt/validateTTNumbers";
import { getTtValidDayOptions } from "@/helpers/getTtValidDayOptions";
import TTPageDetailsModal from "../../modules/bet-details/TTPageDetailsModal.vue";
import TTViewBetModal from "../../modules/entry/TTViewBetModal.vue";
import { useRootStore } from "@/store/pinia";
import { useTranslation } from "@/store/translation";
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute, useRouter } from "vue-router/composables";

type RouteQueryValue = string | (string | null)[] | null | undefined;


export default /*#__PURE__*/_defineComponent({
  __name: 'TTMassEntry2',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const rootStore = useRootStore();
const vm = getCurrentInstance()?.proxy as any;
const { $t } = useTranslation();
const t = (text: string) => $t(text);

const releases = ref<null | Release[]>(null);
const selectedDayIndex = ref(0);
const page = ref("");
const showEditModal = ref(false);
const isSubmitting = ref(false);
const showAccountSelectionModal = ref(false);
const showCopyBetModal = ref(false);
const showViewBetModal = ref(false);
const successPageId = ref<null | TtPage["id"]>(null);
const showPageDetailsModal = ref(false);
const editForm = ref({
  rowsFrom: undefined,
  rowsTo: undefined,
  unit: undefined,
  mode: undefined,
} as TTEditForm);
const entries = ref<TTEntryInput[]>([]);

const selectedAccountId = computed(() => getRouteQueryValue(route.query.userId));
const isSimple = computed(() => route.name === "toto simple entry");
const MAX_ROWS = computed(() => (isSimple.value ? 20 : 40));
const tables = computed(() => {
  if (isSimple.value) {
    return [{ startingIndex: 0 }];
  }
  return [{ startingIndex: 0 }, { startingIndex: 20 }];
});
const NUMBERS_COUNT = computed(() => (isSimple.value ? 4 : 10));
const releasesVariables = computed(() => {
  return {
    appMode: rootStore.appMode,
    weeksBackCount: 0,
  };
});
const mountedReleasesVariables = computed(() => {
  return {
    appMode: rootStore.appMode,
  };
});
const userVariables = computed(() => {
  return {
    userId: selectedAccountId.value,
  };
});
const { result: releasesResult, loading: releasesLoading } = useQuery<{
  releases: Release[];
}>(queries.releases, releasesVariables, {
  fetchPolicy: "cache-and-network",
});
const { result: userResult, loading: userLoading } = useQuery<{ user: User }>(
  queries.userWithCreditSummary,
  userVariables,
  {
    enabled: computed(() => !!selectedAccountId.value),
    fetchPolicy: "cache-and-network",
  }
);

watch(
  releasesResult,
  (result) => {
    if (result?.releases) {
      releases.value = result.releases;
    }
  },
  { immediate: true }
);
watch(isSimple, reinitPage);

const user = computed(() => userResult.value?.user ?? null);
const selectedAccount = computed(() => user.value);
const apolloLoading = computed(() => releasesLoading.value || userLoading.value);
const creditForDisplay = computed(() => {
  return {
    creditGiven: selectedAccount.value?.creditGiven || 0,
    creditUsed: selectedAccount.value?.creditSummary.creditUsed || 0,
    creditBalance: selectedAccount.value?.creditSummary.creditBalance || 0,
  };
});
const selectedDay = computed<TTDay>({
  get() {
    if (releases.value) {
      const selectedOption =
        dayOptions.value[selectedDayIndex.value] ?? dayOptions.value[0];
      return (selectedOption?.value ?? 1) as TTDay;
    }
    return 1;
  },
  set(val) {
    const index = dayOptions.value.findIndex((opt) => opt.value === val);
    selectedDayIndex.value = index === -1 ? 0 : index;
  },
});
const upcomingReleases = computed(() =>
  releases.value?.filter((release) => release.windowIsOpen)
);
const validEntries = computed(() => compact(parseTtEntryInputs(entries.value)));
const calculations = computed(() => calculateTtEntries(compact(validEntries.value)));
const validDayOptions = computed(() => {
  if (!releases.value) {
    return [];
  }
  return getTtValidDayOptions(releases.value);
});
const dayOptions = computed(() => {
  const OPTIONS = [
    { label: "1:Mo", value: 1, defaultPriority: false },
    { label: "4:Th|Fr>", value: 4, defaultPriority: false },
    { label: "5:Mo|Th/Fr", value: 5, defaultPriority: false },
  ];
  return OPTIONS.filter((o) => validDayOptions.value.includes(o.value));
});

  // get totalCost() {
  //   const costs = this.validEntries.map(entry => {
  //     return calculateEntryCost(entry);
  //   });
  //   const bigCosts = costs.reduce((sum, cost) => {
  //     sum += cost.big;
  //     return sum;
  //   }, 0);
  //   const smallCosts = costs.reduce((sum, cost) => {
  //     sum += cost.small;
  //     return sum;
  //   }, 0);

  //   return {
  //     big: bigCosts,
  //     small: smallCosts
  //   };
  // }

onMounted(async () => {
  await initPage(true);
});

function getRouteQueryValue(query: RouteQueryValue) {
  return Array.isArray(query) ? query[0] : query;
}

async function reinitPage() {
  if (isSimple.value) {
    return initPage(false);
  }
  return initPage(true);
}

async function initPage(keepCurrentEntries = false) {
  const result = await apolloClient.query<{ releases: Release[] }>({
    query: queries.releases,
    variables: mountedReleasesVariables.value,
  });
  releases.value = result.data.releases;
  const priorityIndex = dayOptions.value.findIndex((opt) => opt.defaultPriority);
  selectedDayIndex.value = priorityIndex === -1 ? 0 : priorityIndex;
  if (!keepCurrentEntries) {
    entries.value = [];
  }
  for (let i = 0; i < MAX_ROWS.value; i++) {
    entries.value.push({
      day: selectedDay.value,
      number1: "",
      number2: "",
      number3: "",
      number4: "",
      number5: "",
      number6: "",
      number7: "",
      number8: "",
      number9: "",
      number10: "",
      unit: undefined,
      mode: undefined,
    });
  }
}

function handleCopyPage(pageToCopy: TtPage) {
    // const hasAdvanceBet = page.entries.some((e) => {
    //   return e.numbersText.split("-").length > 4;
    // });
    // if (hasAdvanceBet) {
    // copy goes to advance bet
    router.push({
      name: "tt mass entry 2",
      query: { ...route.query },
    });
    // }

    doClearEntries();
    const newEntries = [...entries.value];
    for (const [index, entry] of pageToCopy.entries.entries()) {
      const numbers = entry.numbersText.split("-");
      newEntries[index] = {
        number1: numbers[0] || "",
        number2: numbers[1] || "",
        number3: numbers[2] || "",
        number4: numbers[3] || "",
        number5: numbers[4] || "",
        number6: numbers[5] || "",
        number7: numbers[6] || "",
        number8: numbers[7] || "",
        number9: numbers[8] || "",
        number10: numbers[9] || "",
        day: selectedDay.value,
        unit: entry.unit,
        mode: entry.mode ? entry.mode : undefined,
      };
    }
    entries.value = newEntries;
    page.value = pageToCopy.name;
    return;
  }

function toggleEditModal() {
  showEditModal.value = !showEditModal.value;
}

function changeSelectedDay(newVal: number) {
    for (const entry of entries.value) {
      entry.day = newVal as TTDay;
    }
  }
function changeAllMode(mode: undefined | TtMode.R) {
    for (const entry of entries.value) {
      entry.mode = mode;
    }
  }

function focus(field: "number" | "big" | "small", inputTargetIndex: number) {
    let targetIndex = inputTargetIndex;
    if (targetIndex < 0) {
      targetIndex = 59;
    } else if (targetIndex > 59) {
      targetIndex = 0;
    }
    const tablesEles = vm.$refs["mass-entry-table"] as any;
    for (const [i, table] of tables.value.entries()) {
      const startingIndex = table.startingIndex;
      if (targetIndex >= startingIndex && targetIndex < startingIndex + 20) {
        tablesEles[i].focus(field, targetIndex);
      }
    }
    return;
  }

function performEditRows(newEditForm: TTEditForm) {
    if (!newEditForm.rowsFrom || !newEditForm.rowsTo) {
      return;
    }

    // make sure in range
    const rowsFrom = clamp(newEditForm.rowsFrom, 0, MAX_ROWS.value);
    const rowsTo = clamp(newEditForm.rowsTo, 0, MAX_ROWS.value);

    // make sure rowsFrom is smaller than rowsTo
    const range = [rowsFrom, rowsTo].sort((a, b) => a - b);
    const newEntries = [...entries.value];
    for (let i = range[0] - 1; i <= range[1] - 1; i++) {
      newEntries[i].unit = newEditForm.unit;
      newEntries[i].mode = newEditForm.mode;
    }
    entries.value = newEntries;
    editForm.value = {
      rowsFrom: undefined,
      rowsTo: undefined,
      unit: undefined,
      mode: undefined,
    };
    showEditModal.value = false;
  }

async function clearEntries(event: Event) {
    vm.$confirm.require({
      target: event.currentTarget,
      group: "popups",
      header: "Clear the form?",
      message: "All existing inputs will be deleted.",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        // callback to execute when user confirms the action
        doClearEntries();
      },
      reject: () => {
        // callback to execute when user rejects the action
      },
    });
  }

function doClearEntries() {
    const newEntries = [];

    for (let i = 0; i < MAX_ROWS.value; i++) {
      newEntries.push({
        day: selectedDay.value,
        number1: "",
        number2: "",
        number3: "",
        number4: "",
        number5: "",
        number6: "",
        number7: "",
        number8: "",
        number9: "",
        number10: "",
        unit: undefined,
        mode: undefined,
      });
    }
    entries.value = newEntries;
  }

function validateAllAndShowError() {
    const errors = [];
    for (const [index, entry] of entries.value.entries()) {
      const numbersValid = validateTTNumbers(entry, false);
      const noUnit =
        (entry.unit as any) === "" ||
        entry.unit === undefined ||
        entry.unit === 0;
      const numbers = [
        entry.number1,
        entry.number2,
        entry.number3,
        entry.number4,
        entry.number5,
        entry.number6,
        entry.number7,
        entry.number8,
        entry.number9,
        entry.number10,
      ];
      if (compact(numbers).length === 0 && noUnit) {
        // if (compact(numbers).length === 0 && noUnit && !hasMode) {
        continue;
      }
      if (!numbersValid) {
        errors.push(`Row ${index + 1} has invalid numbers`);
      }
      if (numbersValid && noUnit) {
        errors.push(`Row ${index + 1} has no unit`);
      }
    }
    return errors;
  }

async function handleSubmit() {
    const errors = validateAllAndShowError();
    if (errors.length) {
      vm.$toast.add({
        severity: "error",
        summary: "Error",
        detail: errors[0],
        life: 3000,
      });
      return;
    }

    if (!selectedAccountId.value) {
      vm.$toast.add({
        severity: "error",
        summary: "Error",
        detail: "No account selected",
        life: 3000,
      });
      return;
    }

    const parsedEntries = validEntries.value;

    if (!parsedEntries.length) {
      vm.$toast.add({
        severity: "error",
        summary: "Error",
        detail: "Please enter number details for entries",
        life: 3000,
      });
      return;
    }

    vm.$confirm.require({
      header: "Confirm?",
      message: t("Are you sure to submit the entries for TOTO betting?"),
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          isSubmitting.value = true;
          const result = await apolloClient.mutate({
            mutation: mutations.insertTtEntry,
            variables: {
              page: {
                page: page.value,
                entries: parsedEntries,
                userId: selectedAccountId.value,
                entrySource: "massEntry",
              },
            },
          });
          successPageId.value = result.data.insertTtEntry;
          showPageDetailsModal.value = true;
          doClearEntries();
          page.value = "";
        } catch (err) {
          errorMutationNotification(err);
        } finally {
          isSubmitting.value = false;
        }
      },
      reject: () => {
        // Optional: Handle rejection
      },
    });
  }


return { __sfc: true,route, router, rootStore, vm, $t, t, releases, selectedDayIndex, page, showEditModal, isSubmitting, showAccountSelectionModal, showCopyBetModal, showViewBetModal, successPageId, showPageDetailsModal, editForm, entries, selectedAccountId, isSimple, MAX_ROWS, tables, NUMBERS_COUNT, releasesVariables, mountedReleasesVariables, userVariables, releasesResult, releasesLoading, userResult, userLoading, user, selectedAccount, apolloLoading, creditForDisplay, selectedDay, upcomingReleases, validEntries, calculations, validDayOptions, dayOptions, getRouteQueryValue, reinitPage, initPage, handleCopyPage, toggleEditModal, changeSelectedDay, changeAllMode, focus, performEditRows, clearEntries, doClearEntries, validateAllAndShowError, handleSubmit, TTMassEntryTable2, TTEditRowsModal, AccountSelectModal, TtMode, TTCopyBetModal, formatDollar, TTPageDetailsModal, TTViewBetModal }
}

})